<template>
<div id="purchase" v-if="showPurchase">
  <spinner
    size="large"
    v-if="base.loading"
  />
  <div
    v-if="purchase.id && !hideHeader"
  >
    <h2>
      Purchase #{{ purchase.id }}
      <span
        v-if="purchase.gift"
      >
        {{ $root.giftIcon }}
      </span>
    </h2>

    <purchase-nav
      :purchaseId="purchase.id"
    ></purchase-nav>

    <h3>
      Purchase Details
      <purchase-requests
        :purchaseId="purchase.id"
      />
    </h3>
  </div>

  <table
    class="table"
  >
    <tbody>
      <tr>
        <td>
          Purchased At
        </td>
        <td>
          {{ formattedPurchasedAt }}
          <span
            v-if="purchase.gift"
          >
            {{ $root.giftIcon }}
          </span>
        </td>
      </tr>
      <tr
        v-if="!showPaymentOnly"
      >
        <td>
          Gifted?
        </td>
        <td>
          <span
            class="badge badge-sm badge-primary"
          >
            <span
              v-if="isGift"
            >
              Yes 🎁
            </span>
            <span
              v-else
            >
              No
            </span>
          </span>
        </td>
      </tr>
      <tr
        v-if="!showPaymentOnly"
      >
        <td>
          Send At
        </td>
        <td>
          {{ formattedSendAt }}
        </td>
      </tr>
      <tr
        v-if="!showPaymentOnly"
      >
        <td>
          Sent At
        </td>
        <td>
          {{ formattedSentAt }}
        </td>
      </tr>
      <tr>
        <td>
          Status
        </td>
        <td>
          <purchase-status-badge
            :status="purchase.status"
          />

          <button
            type="submit"
            class="btn btn-warning btn-sm ml-1"
            v-on:click.prevent="refresh"
            v-if="isExternalPurchase"
          >
            Refresh
            <spinner
              size="small"
              v-if="base.loading"
            />
          </button>
        </td>
      </tr>
      <tr>
        <td>
          Purchase Email
        </td>
        <td>
          <a v-bind:href=" 'mailto:' + purchase.customer_email">{{ purchase.customer_email }}</a>
        </td>
      </tr>
    </tbody>
    <tbody
      v-if="!showPaymentOnly"
    >
      <tr>
        <td>
          Recipient Email
        </td>
        <td v-if="purchase.recipient_user">
          <router-link :to="{
              name: 'user',
              params: {
                userId: purchase.recipient_user.id
              }
            }
          ">
            {{ purchase.recipient_email }}
          </router-link>
          <preview-purchase-email
            lbl="Raw"
            :url="apiUrl(this.purchaseUrl + '/preview_email', true)"
            pre
            v-if="!paymentOnly"
          />
        </td>
      </tr>
      <tr>
        <td>
          Buyer
        </td>
        <td v-if="purchase.buyer_user">
          <router-link :to.lazy="{
            name: 'user',
            params: { userId: purchase.buyer_user.id }
          }">
            {{ purchase.buyer_user.username }} #{{ purchase.buyer_user.id }}
          </router-link>
        </td>
        <td v-else>
          No buyer
        </td>
      </tr>
      <tr v-if="giftRecipient">
        <td>
          Recipient
        </td>
        <td>
          <router-link :to="{ name: 'user', params: { userId: purchase.recipient_user.id }}">
            {{ purchase.recipient_user.username }} #{{ purchase.recipient_user.id }}
          </router-link>
        </td>
      </tr>
      <tr>
        <td>
          Purchase Notes
        </td>
        <td v-if="purchase.notes">
          {{ purchase.notes }}
        </td>
        <td v-else>
          No purchase notes.
        </td>
      </tr>
      <tr>
        <td>
          Internal Purchase Notes
        </td>
        <td v-if="purchase.internal_notes">
          {{ purchase.internal_notes }}
        </td>
        <td v-else>
          No internal purchase notes.
        </td>
      </tr>
      <tr
        v-if="purchase.download_url"
      >
        <td>Download Count</td>
        <td>
          <a
            :href="purchase.download_url"
            target="_blank"
          >
            Accessed {{ purchase.download_access_count || 0 }} times
          </a>
        </td>
      </tr>
      <tr>
        <td>
          Imported?
        </td>
        <td>
          <div
            class="badge badge-sm badge-primary"
          >
            {{ purchase.imported_purchase_id ? 'Yes' : 'No' }}
          </div>
        </td>
      </tr>
      <tr>
        <td>
          Purchase Record Created At
        </td>
        <td>
          {{ $root.displayDateTimeFmt(purchase.created_at) }}
        </td>
      </tr>
      <tr>
        <td>
          Purchase Record Last Updated At
        </td>
        <td>
          {{ $root.displayDateTimeFmt(purchase.updated_at) }}
        </td>
      </tr>
      <tr>
        <td>
          Purchase Token
        </td>
        <td>
          <a :href="purchase.download_url">
            {{ purchase.download_token || 'n/a' }}
          </a>
        </td>
      </tr>
      <tr>
        <td>
          Tags
        </td>
        <td v-if="purchase.tags.length">
          <span
            v-for="tag in purchase.tags"
            v-bind:key="tag.id"
            v-if="tag"
          >
            <router-link
              :to="{
                name: 'tag',
                params: {
                  tagId: tag.id
                }
              }"
              class="btn btn-primary btn-sm tag mr-2"
            >
              {{ tag.name }}
            </router-link>
          </span>
        </td>
        <td v-else>
          No tags
        </td>
      </tr>
    </tbody>
    <purchase-timestamps
    />
  </table>

  <hr />

  <h3>Purchased Items</h3>

  <p
    v-if="!purchase.purchasable_items.length"
  >
    No purchased items
  </p>
  <div
    class="text-center alert alert-danger"
    v-if="needsFixing"
  >
    <p
    >
      This record has a subscription, but no purchasable items.
      Fixing this record will assign the subscription purchasable item
      to the purchase.
    </p>

    <button
      class="btn btn-danger btn-block"
      v-on:click.prevent="updatePurchaseItems"
    >
      Fix Record
      <spinner
        size="small"
        v-if="base.loading"
      />
    </button>
  </div>
  <ol
    v-else
    v-for="purchasable_item in purchase.purchasable_items"
  >
    <li
      v-if="purchasable_item"
    >
      <router-link :to="{ name: 'purchasable_item', params: { purchasableItemId: purchasable_item.id }}">
        {{ purchasable_item.name }}: ${{ purchasable_item.price }}
      </router-link>
    </li>
  </ol>

  <hr />

  <div v-if="purchase.subscriptions.length">
    <h3>Purchased Subscriptions</h3>

    <ol
      v-for="subscription in purchase.subscriptions"
    >
      <li>
        <router-link :to="{ name: 'subscription', params: { subscriptionId: subscription.id }}">
          {{ subscription.purchasable_item.name }}:
          {{ formattedTime(subscription.start_ts) }}-{{ formattedTime(subscription.end_ts) }}
        </router-link>
      </li>
    </ol>
  </div>

  <h3>Financial Details</h3>

  <table class="table">
    <tr>
      <td>Gross</td>
      <td>${{ purchase.gross }}</td>
    </tr>
    <tr>
      <td>Fee</td>
      <td>${{ purchase.fee }}</td>
    </tr>
    <tr>
      <td>Net</td>
      <td>${{ purchase.net }}</td>
    </tr>
  </table>

  <button
    class="btn btn-primary btn-block"
    v-on:click.prevent="addPayment"
    v-if="!purchase.id"
  >
    Create Purchase From Payment
    <spinner
      size="small"
      v-if="base.loading"
    />
  </button>

  <hr />

  <div>
    <h3>
      Technical Details
      <span
        v-if="!showPaymentOnly && purchase.merchant_code"
      >
        <button
          class="btn btn-warning btn-sm ml-1"
          v-on:click.prevent="togglePurchasePaymentsDisplay"
        >
          <span
            v-if="showPurchaseDetails"
          >
            Hide
          </span>
          <span
          >
            Show
          </span> Details
        </button>

        <button
          type="submit"
          class="btn btn-warning btn-sm ml-1"
          data-toggle="modal" data-target="#paymentDataModal"
          v-if="isExternalPurchase && !paymentOnly"
          v-on:click="openPaymentDataModal"
        >
          Get {{ paymentLbl }} Data
          <spinner
            size="small"
            v-if="base.loading"
          />
        </button>
      </span>
    </h3>

    <p
      class="alert alert-danger text-center"
      v-if="!hasPaymentInfo"
    >
      No Payment Info Available
    </p>

    <payments-modal
      :purchaseId="purchase.id"
    />

    <purchase-payments-details
      :purchase="paymentDetails"
      v-if="showPurchaseDetails"
    />
  </div>

  <div
    class="form-group"
    v-if="!paymentOnly"
  >
    <button
      type="button"
      class="btn btn-block btn-primary form-control"
      v-on:click="resendRecipientEmail"
    >
      <span>Resend Recipient Email</span>
      <spinner size="small" v-if="base.loading"></spinner>
    </button>

    <button
      type="button"
      class="btn btn-block btn-primary form-control"
      v-if="purchase.gift"
      v-on:click="resendBuyerEmail"
    >
      Resend Buyer Email
      <spinner size="small" v-if="base.loading"></spinner>
    </button>

    <button
      type="button"
      class="btn btn-block btn-danger form-control"
      v-if="$root.isDev"
      v-on:click="deletePurchase"
    >
      Delete Purchase
      <spinner size="small" v-if="base.loading"></spinner>
    </button>

  </div>
</div>
</template>

<script>
import PreviewPurchaseEmail from '@/components/PreviewRecipientEmail'
import PurchaseNav from '@/components/PurchaseNav'
import PurchasePaymentsModal from '@/components/PurchasePaymentsModal'
import PurchasePaymentsDetails from '@/components/PurchasePaymentsDetails'
import PurchaseRequests from '@/components/PurchaseRequests'
import PurchaseStatusBadge from '@/components/PurchaseStatusBadge'
import PurchaseTimestamps from '@/components/TimestampRows'

import PurchaseMixin from '@/mixins/PurchaseMixin'

import FlashStore from '@/stores/FlashStore'

import Spinner from 'vue-simple-spinner'

import { mapState } from 'vuex'

export default {
  data () {
    return {
      loading: false,
      paymentJSON: undefined,
      payments: undefined,
      hidePurchasePaymentsDisplay: false,
      forcePurchasePaymentsDisplay: false
    }
  },

  props: {
    'allowRefresh': {
      type: Boolean,
      default: false
    },

    'hideHeader': {
      type: Boolean,
      default: false
    },

    'noLookup': {
      type: Boolean,
      default: false
    },

    'paymentOnly': {
      type: Boolean,
      default: false
    },

    'purchaseId': {
      type: [ Number, String ],
      default: undefined
    }
  },

  computed: mapState({
    hasPaymentInfo () {
      return (
        this.purchase.merchant_code ||
          this.purchase.external_payment_sale_id ||
          this.purchase.external_payment_id
      )
    },

    showPaymentOnly () {
      return this.paymentOnly && !this.purchase.id
    },

    showPurchase () {
      return this.purchase.id || this.purchase.purchased_at
    },

    isGift () {
      return this.purchase.gift === true
    },

    isExternalPurchase () {
      return this.purchase.external_payment_id || this.purchase.external_payment_sale_id
    },

    formattedPurchasedAt () {
      if (this.purchase.purchased_at) {
        return this.$root.displayDateTimeFmt(this.purchase.purchased_at)
      }
    },

    formattedSendAt () {
      if (this.purchase.send_at) {
        return this.$root.displayDateTimeFmt(this.purchase.send_at)
      }

      if (this.isImported) {
        return 'n/a'
      }
    },

    formattedSentAt () {
      if (this.purchase.sent_at) {
        return this.$root.displayDateTimeFmt(this.purchase.sent_at)
      }

      if (this.isImported) {
        return 'n/a'
      }
    },

    isImported () {
      return this.purchase.imported_purchase_id
    },

    isPurchaseProcesssed () {
      return this.purchase.status === 'completed' || (
        this.purchase.external_payment_sale_id !== undefined
      )
    },

    isRecurringPurchase () {
      return this.purchase.external_payment_subscription_id
    },

    giftRecipient () {
      if (this.purchase.buyer_user && this.purchase.recipient_user) {
        return this.purchase.buyer_user.id !== this.purchase.recipient_user.id
      }
    },

    needsFixing () {
      return !this.purchase.purchasable_items.length && this.purchase.subscriptions.length
    },

    paymentDetails () {
      return {
        external_payment_id: this.purchase.external_payment_id,
        external_payment_sale_id: this.purchase.external_payment_sale_id,
        external_payment_subscription_id: this.purchase.external_payment_subscription_id,
        external_payment_url: this.purchase.external_payment_url,
        external_payment_token: this.purchase.external_payment_token,
        external_payer_id: this.purchase.external_payer_id,
        merchant_code: this.purchase.merchant_code,
        is_external_purchase: this.purchase.isExternalPurchase,
        merchant_email: this.purchase.merchant_email,
        sandbox: this.purchase.sandbox
      }
    },

    paymentLbl () {
      return (this.isRecurringPurchase ? 'Recurring Payments' : 'Payment')
    },

    purchaseUrl () {
      return '/purchases/' + this.purchase.id
    },

    showPurchaseDetails () {
      return (
        this.hasPaymentInfo && this.isPurchaseProcesssed && (
          !this.hidePurchasePaymentsDisplay ||
            this.forcePurchasePaymentsDisplay
        )
      )
    },

    base: state => state.base,
    purchase: state => state.PurchaseModule.purchase
  }),

  components: {
    'payments-modal': PurchasePaymentsModal,
    'preview-purchase-email': PreviewPurchaseEmail,
    'purchase-nav': PurchaseNav,
    'purchase-payments-details': PurchasePaymentsDetails,
    'purchase-requests': PurchaseRequests,
    'purchase-status-badge': PurchaseStatusBadge,
    'purchase-timestamps': PurchaseTimestamps,
    'spinner': Spinner
  },

  mixins: [
    PurchaseMixin
  ],

  created () {
    if (!this.noLookup) {
      this.get()
      this.$on('update-purchase', this.get)
    }
  },

  methods: {
    addPayment () {
      if (
        confirm(
          'This will create a purchase from the PayPal transaction. Do you want to continue?'
        )
      ) {
        this.purchase.purchasable_item_ids = this.purchase.purchasable_items.map(item => {
          return item.id
        })
        this.purchase.suppress_email = true

        this.$store.dispatch(
          'PurchaseModule/createPurchaseByPayment', this.purchase
        ).catch(this.$root.processErrors)
      }
    },

    deletePurchase () {
      const confirmation = 'CONFIRM'
      const prompted = prompt(
        `This will permanently delete this purchase. Are you sure you want to continue? Enter '${confirmation}' to delete.`
      )

      if (prompted === confirmation) {
        this.$store.dispatch(
          'PurchaseModule/deletePurchase', this.purchase.id
        ).then(() => {
          this.$router.push({
            name: 'purchases'
          })

          FlashStore.result('This purchase has been deleted.')
        }).catch(this.$root.processErrors)
      } else {
        alert('Deletion confirmation failed!')
      }
    },

    formattedTime (d) {
      return this.$root.displayDateTimeFmt(d)
    },

    togglePurchasePaymentsDisplay () {
      this.forcePurchasePaymentsDisplay = !this.forcePurchasePaymentsDisplay
      this.hidePurchasePaymentsDisplay = !this.forcePurchasePaymentsDisplay
    },

    get () {
      this.$store.dispatch(
        'PurchaseModule/getPurchase', this.purchaseId
      ).catch(this.$root.processErrors)
    },

    openPaymentDataModal () {
      this.$emit('open-payment-data-modal', this.purchase)
    },

    processErrors (resp) {
      this.$parent.$emit('processErrors', resp)
    },

    resendRecipientEmail () {
      if (confirm('Are you sure you want to resend this email to the recipient?')) {
        this.loading = true
        const resendUrl = this.apiUrl('/purchases/' + this.purchase.id + '/resend_recipient_email', true)

        this.$http.post(resendUrl).then((resp) => {
          FlashStore.result('Email sent.')
        }, this.processErrors
        ).finally(() => {
          this.loading = false
        })
      }
    },

    resendBuyerEmail () {
      this.loading = true
      const resendUrl = this.apiUrl('/purchases/' + this.purchase.id + '/resend_buyer_email', true)

      this.$http.post(resendUrl).then((resp) => {
        FlashStore.result('Email sent.')
      }, this.processErrors
      ).finally(() => {
        this.loading = false
      })
    },

    refresh () {
      if (
        confirm(
          'This will reset any payment-specific changes made to this record! Are you sure you want to continue?'
        )
      ) {
        this.$store.dispatch(
          'PurchaseModule/refreshPurchase', this.purchase.id
        ).catch(this.$root.processErrors)
      }
    },

    updatePurchaseItems () {
      this.$store.dispatch(
        'PurchaseModule/refreshPurchasableItems', this.purchase.id
      ).catch(this.$root.processErrors)
    }
  }
}
</script>

<style scoped>
td {
  max-width: 40%;
  overflow: hidden;
  overflow-wrap: break-word;
  word-wrap: break-word;
}

#purchase-requests {
  display: inline-block;
  float: right;
}
</style>



// WEBPACK FOOTER //
// src/components/Purchase.vue